import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface DocumentNumberSeries {
  id: string;
  name: string;
  documentType: string;
  prefix?: string;
  suffix?: string;
  startNumber: number;
  currentNumber: number;
  padding: number;
  useFiscalYear: boolean;
  fiscalYear?: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  creator?: {
    firstName: string;
    lastName: string;
  };
}

const DOCUMENT_TYPES = [
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'PURCHASE_INVOICE', label: 'Purchase Invoice' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'JOURNAL_ENTRY', label: 'Journal Entry' },
  { value: 'PURCHASE_ORDER', label: 'Purchase Order' },
  { value: 'GRN', label: 'GRN' },
];

const DocumentNumberSeriesPage: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions(user);
  const [series, setSeries] = useState<DocumentNumberSeries[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<DocumentNumberSeries | null>(null);
  const [filterDocumentType, setFilterDocumentType] = useState<string>('');

  const [formData, setFormData] = useState({
    name: '',
    documentType: 'INVOICE',
    prefix: '',
    suffix: '',
    startNumber: 1,
    padding: 3,
    useFiscalYear: false,
    fiscalYear: new Date().getFullYear().toString(),
    isDefault: false,
    isActive: true,
  });

  useEffect(() => {
    fetchSeries();
  }, [filterDocumentType]);

  const fetchSeries = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (filterDocumentType) {
        params.documentType = filterDocumentType;
      }
      const response = await axios.get('/api/erp/document-number-series', { params });
      setSeries(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching document number series:', err);
      toast.error('Failed to load document number series');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await axios.post('/api/erp/document-number-series', formData);
      if (response.data.success) {
        toast.success('Document number series created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchSeries();
      } else {
        toast.error(response.data.error || 'Failed to create document number series');
      }
    } catch (err: any) {
      console.error('Error creating document number series:', err);
      toast.error(err.response?.data?.error || 'Failed to create document number series');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSeries) return;

    try {
      const response = await axios.put(`/api/erp/document-number-series/${selectedSeries.id}`, formData);
      if (response.data.success) {
        toast.success('Document number series updated successfully');
        setShowEditModal(false);
        setSelectedSeries(null);
        resetForm();
        fetchSeries();
      } else {
        toast.error(response.data.error || 'Failed to update document number series');
      }
    } catch (err: any) {
      console.error('Error updating document number series:', err);
      toast.error(err.response?.data?.error || 'Failed to update document number series');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document number series? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/erp/document-number-series/${id}`);
      toast.success('Document number series deleted successfully');
      fetchSeries();
    } catch (err: any) {
      console.error('Error deleting document number series:', err);
      toast.error(err.response?.data?.error || 'Failed to delete document number series');
    }
  };

  const handleEdit = (seriesItem: DocumentNumberSeries) => {
    setSelectedSeries(seriesItem);
    setFormData({
      name: seriesItem.name,
      documentType: seriesItem.documentType,
      prefix: seriesItem.prefix || '',
      suffix: seriesItem.suffix || '',
      startNumber: seriesItem.startNumber,
      padding: seriesItem.padding,
      useFiscalYear: seriesItem.useFiscalYear,
      fiscalYear: seriesItem.fiscalYear || new Date().getFullYear().toString(),
      isDefault: seriesItem.isDefault,
      isActive: seriesItem.isActive,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      documentType: 'INVOICE',
      prefix: '',
      suffix: '',
      startNumber: 1,
      padding: 3,
      useFiscalYear: false,
      fiscalYear: new Date().getFullYear().toString(),
      isDefault: false,
      isActive: true,
    });
    setSelectedSeries(null);
  };

  const generatePreview = (seriesItem: DocumentNumberSeries): string => {
    const paddedNumber = String(seriesItem.currentNumber + 1).padStart(seriesItem.padding, '0');
    let preview = '';

    if (seriesItem.prefix) {
      preview += seriesItem.prefix;
    }

    preview += paddedNumber;

    if (seriesItem.suffix) {
      preview += seriesItem.suffix;
    }

    if (seriesItem.useFiscalYear && seriesItem.fiscalYear) {
      preview += `-FY${seriesItem.fiscalYear.slice(-2)}`;
    }

    return preview || 'No preview available';
  };

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!hasPermission('erp_financial_report', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view document number series.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Document Number Series</h1>
            <PageHelp pageIdentifier="document-number-series" title="Document Number Series" />
          </div>
          <p className="text-gray-600 mt-1">Customize how document numbers are generated</p>
        </div>
        {hasPermission('erp_financial_report', 'write') && (
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Create Series
          </button>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Document Type
        </label>
        <select
          value={filterDocumentType}
          onChange={(e) => setFilterDocumentType(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2"
        >
          <option value="">All Types</option>
          {DOCUMENT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : series.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No document number series found. Create your first series to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Format</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {series.map((seriesItem) => (
                <tr key={seriesItem.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="flex items-center">
                      <span className="font-medium">{seriesItem.name}</span>
                      {seriesItem.isDefault && (
                        <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Default</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">{DOCUMENT_TYPES.find(t => t.value === seriesItem.documentType)?.label || seriesItem.documentType}</td>
                  <td className="px-4 py-2 font-mono text-sm">{generatePreview(seriesItem)}</td>
                  <td className="px-4 py-2">{seriesItem.currentNumber}</td>
                  <td className="px-4 py-2 font-mono">{seriesItem.currentNumber + 1}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      seriesItem.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {seriesItem.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      {hasPermission('erp_financial_report', 'write') && (
                        <button
                          onClick={() => handleEdit(seriesItem)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {hasPermission('erp_financial_report', 'delete') && (
                        <button
                          onClick={() => handleDelete(seriesItem.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">Create Document Number Series</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                  <select
                    value={formData.documentType}
                    onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prefix</label>
                  <input
                    type="text"
                    value={formData.prefix}
                    onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., INV-"
                  />
                  <p className="text-xs text-gray-500 mt-1">Text to appear before the number</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suffix</label>
                  <input
                    type="text"
                    value={formData.suffix}
                    onChange={(e) => setFormData({ ...formData, suffix: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., -FY24"
                  />
                  <p className="text-xs text-gray-500 mt-1">Text to appear after the number</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Number</label>
                    <input
                      type="number"
                      value={formData.startNumber}
                      onChange={(e) => setFormData({ ...formData, startNumber: parseInt(e.target.value) || 1 })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Padding</label>
                    <input
                      type="number"
                      value={formData.padding}
                      onChange={(e) => setFormData({ ...formData, padding: parseInt(e.target.value) || 3 })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      min="1"
                      max="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">Number of digits (e.g., 3 = 001, 4 = 0001)</p>
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.useFiscalYear}
                      onChange={(e) => setFormData({ ...formData, useFiscalYear: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Use Fiscal Year Series</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Reset series each fiscal year</p>
                  {formData.useFiscalYear && (
                    <input
                      type="text"
                      value={formData.fiscalYear}
                      onChange={(e) => setFormData({ ...formData, fiscalYear: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 mt-2"
                      placeholder="e.g., 2024"
                    />
                  )}
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isDefault}
                      onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Set as Default</span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedSeries && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">Edit Document Number Series</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                  <select
                    value={formData.documentType}
                    onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                    disabled
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Document type cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prefix</label>
                  <input
                    type="text"
                    value={formData.prefix}
                    onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., INV-"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suffix</label>
                  <input
                    type="text"
                    value={formData.suffix}
                    onChange={(e) => setFormData({ ...formData, suffix: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., -FY24"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Number</label>
                    <input
                      type="number"
                      value={formData.startNumber}
                      onChange={(e) => setFormData({ ...formData, startNumber: parseInt(e.target.value) || 1 })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Number</label>
                    <input
                      type="number"
                      value={selectedSeries.currentNumber}
                      onChange={(e) => setFormData({ ...formData, currentNumber: parseInt(e.target.value) || 0 })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Manually adjust current number if needed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Padding</label>
                    <input
                      type="number"
                      value={formData.padding}
                      onChange={(e) => setFormData({ ...formData, padding: parseInt(e.target.value) || 3 })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      min="1"
                      max="10"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.useFiscalYear}
                      onChange={(e) => setFormData({ ...formData, useFiscalYear: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Use Fiscal Year Series</span>
                  </label>
                  {formData.useFiscalYear && (
                    <input
                      type="text"
                      value={formData.fiscalYear}
                      onChange={(e) => setFormData({ ...formData, fiscalYear: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 mt-2"
                      placeholder="e.g., 2024"
                    />
                  )}
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isDefault}
                      onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Set as Default</span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentNumberSeriesPage;
